<script>
export const group = 'Bottom'
</script>

<script setup>
import TextSlideUp from './text_animations/TextSlideUp.vue'
import CharacterEnlarge from './text_animations/CharacterEnlarge.vue'
import CharacterJumpFromTheRight from './text_animations/CharacterJumpFromTheRight.vue'
import { computed } from 'vue'
import { frames } from '../../../utils/frames'
const gsapTimeline = useGsapTimeline()
const { item } = defineProps({
  item: Object,
})

const timelineSubtitle = ref(null)
const timelineSubtitleOut = ref(null)
const timelineTitle = ref(null)
const timelineNextAttempt = ref(null)
const timelineAnswers = ref(null)

const answersListElement = ref(null)

const answersGap = 24
const answersCardHeight = 168

const viewportHeight = answersCardHeight * 3 + answersGap * 2 + 100 // +100 fixes scaling on HD output, in debug preview this looks incorrect.

const buildAnswersScrollTimeline = () => {
  const answerElements = Array.from(answersListElement.value.children)

  const viewportCenter = viewportHeight / 2

  const centers = answerElements.map((el) => el.offsetTop + el.getBoundingClientRect().height / 2)
  const yForIndex = (i) => viewportCenter - centers[i]

  const setFocus = (focusIndex) => {
    return {
      opacity: (i) => {
        const d = Math.abs(i - focusIndex)
        if (d === 0) return 1
        if (d === 1) return 0.35
        return 0.12
      },
      scale: (i) => (i === focusIndex ? 1 : 0.98),
      duration: frames(10),
      ease: 'power2.out',
    }
  }

  const timeline = gsapTimeline({ name: 'EscapeRoomMostRecentAttemptSmall - Answers Scroll' })
    .set(answersListElement.value, { y: yForIndex(0) })
    .set(answerElements, { opacity: 0.12, scale: 0.98, transformOrigin: 'center center' })
    .to(answerElements, setFocus(0), 0)
    .to({}, { duration: frames(50) })

  for (let i = 1; i < answerElements.length; i++) {
    timeline
      .to(answersListElement.value, { y: yForIndex(i), duration: frames(14), ease: 'power2.inOut' }, '>-0.08')
      .to(answerElements, setFocus(i), '<')
      .to({}, { duration: frames(50) })
  }

  return timeline
}

onMounted(() => {
  gsapTimeline({ name: 'EscapeRoomMostRecentAttemptSmall' })
    .add(timelineTitle)
    .add(timelineSubtitle, `<${frames(14)}`)
    .add(timelineNextAttempt, `<${frames(4)}`)
    .from(answersListElement.value.children, { xPercent: 100, stagger: frames(2), ease: 'power2.out' }, '<')

  timelineAnswers.value = buildAnswersScrollTimeline()
})

const { onDisappear } = useDisappearable()
onDisappear(async () => {
  timelineTitle.value.reverse()
  timelineNextAttempt.value.reverse()

  timelineAnswers.value.kill()

  await gsapTimeline({ name: 'EscapeRoomMostRecentAttemptSmall - End' })
    .add(timelineSubtitleOut.value(), '<')
    .add(timelineNextAttempt, '<')
    .add(timelineTitle, '<')
    .to(answersListElement.value.children, { xPercent: 100, stagger: frames(2), ease: 'power2.in', duration: frames(4) }, '<')
})

const mostRecentAttempt = computed(() => item.enrichment.mostRecentAttempt)
const nextAttempt = computed(() => item.enrichment.nextAttempt)
</script>

<template>
  <div class="absolute bottom-0 p-safe z-10 w-full flex justify-between items-end">
    <div class="text-white space-y-2 flex flex-col items-start">
      <div>
        <TextSlideUp :text="`De vorige poging`" class="text-[4rem] font-bold leading-none text-q-cr-red-primary" @timeline="timelineSubtitle = $event" @timelineOut="timelineSubtitleOut = $event" />
        <CharacterJumpFromTheRight :text="`Muzikale Code`" class="text-white mb-1" @timeline="timelineTitle = $event" />
      </div>
      <CharacterEnlarge
        :text="nextAttempt ? `De volgende poging is ${nextAttempt.humanReadableDate}` : ''"
        class="text-white font-cervo font-bold text-[3rem]"
        @timeline="timelineNextAttempt = $event"
      />
    </div>
    <div ref="answersViewportElement" class="answers-viewport-fade -mb-64 w-[48rem] overflow-hidden" :style="{ height: `${viewportHeight}px` }">
      <div ref="answersListElement" class="flex flex-col" :style="{ gap: `${answersGap}px` }">
        <div v-for="answer in mostRecentAttempt.answers" :key="answer.contestantName" class="bg-white/90 rounded-xl text-6xl flex" :style="{ height: answersCardHeight }">
          <img :src="answer.contestantImageUrl" class="w-40 bg-red-400 object-cover" />
          <div class="p-6">
            <div class="line-clamp-1 font-bold pt-1">{{ answer.title }}</div>
            <div class="line-clamp-1">{{ answer.artist }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.answers-viewport-fade {
  mask-image: linear-gradient(to bottom, transparent 12%, rgb(0 0 0) 30%, rgb(0 0 0) 70%, transparent 88%);
  mask-repeat: no-repeat;
  mask-size: 100% 100%;
}
</style>
